import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Avatar,
  IconButton,
  CircularProgress,
  useMediaQuery,
  Container,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import useFetchRecruiter from "hooks/useFetchRecruiter";
import useUserId from "hooks/useUserId";
import { useFilteredFormFields } from "hooks/useFilteredFormFields";
import { useTranslation } from "react-i18next";
import bg_profile from "../../assets/img/bg_profile.svg";
import colors from "colors/color";
import edit_icon from "../../assets/img/edit_icon.svg";
import cancel_icon from "../../assets/img/cancel_icon.svg";
import RecruiterPasswordSettings from "./RecruiterPasswordSettings";
import { useProfileForm } from "hooks/useProfileForm";

const CancelButton = styled(Button)(({ theme }) => ({
  width: "100%",
  maxWidth: 115,
  height: 48,
  borderRadius: "10px",
  borderWidth: "1px",
  gap: "10px",
  paddingTop: "14px",
  paddingRight: "24px",
  paddingBottom: "14px",
  paddingLeft: "24px",
  border: "1px solid #FF0000",
  color: "#FF0000",
  "&:hover": {
    backgroundColor: "#FF0000",
    color: "#fff",
  },
}));

const UpdateButton = styled(Button)(({ theme }) => ({
  width: "100%",
  maxWidth: 183,
  height: 48,
  borderRadius: "10px",
  borderWidth: "1px",
  paddingTop: "14px",
  paddingRight: "24px",
  paddingBottom: "14px",
  paddingLeft: "24px",
  gap: "10px",
  backgroundColor: colors.primary,
  color: "white",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#0085FF",
  },
  "&.Mui-disabled": {
    backgroundColor: "#e0e0e0",
  },
}));

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
}));

const ProfileSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "#fff",
  borderRadius: "15px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
}));

const CompanyInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
  marginLeft: theme.spacing(5),
  [theme.breakpoints.down("md")]: {
    marginLeft: theme.spacing(2),
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
}));

const CompanyLogoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
}));

const CompanyLogo = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: "50%",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  border: "2px solid #fff",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
}));

const LogoAvatar = styled(Avatar)(({ theme }) => ({
  width: "100%",
  height: "100%",
  fontSize: "16px",
  fontWeight: "bold",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  maxWidth: 310,
  height: 48,
  gap: "10px",
  borderRadius: "10px",
  border: "1px",
  paddingTop: "15px",
  paddingRight: "20px",
  paddingBottom: "15px",
  paddingLeft: "20px",

  "& .MuiOutlinedInput-root": {
    borderRadius: 10,
    backgroundColor: "#fff",
    borderColor: "#D9D9D9",
  },
}));

const UpdatePasswordButton = styled(Button)(({ theme }) => ({
  width: "100%",
  maxWidth: 183,
  height: 48,
  gap: "10px",
  borderRadius: "10px",
  borderWidth: "1px",
  paddingTop: "14px",
  paddingRight: "24px",
  paddingBottom: "14px",
  paddingLeft: "24px",

  "&:hover": {
    backgroundColor: "#303f9f",
    color: "#fff",
  },
  "&:disabled": {
    color: "#fff",
    opacity: 0.2,
  },
}));

const ProfileImageContainer = styled(Box)({
  position: "relative",
});

function User() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const fileInputRef = useRef(null);
  const userId = useUserId();
  const {
    formData: initialFormData,
    loading: recruiterLoading,
    error: recruiterError,
  } = useFetchRecruiter();
  const [formData, setFormData] = useState({});
  const [originalFormData, setOriginalFormData] = useState(null);
  const { isEditing, loading, error, handleEdit, handleSave, setIsEditing } =
    useProfileForm();
  const { t } = useTranslation();

  const filteredFields = useFilteredFormFields(formData);

  useEffect(() => {
    console.log("formData", formData);
  }, [formData]);
  useEffect(() => {
    if (initialFormData) {
      setFormData(initialFormData);
      setOriginalFormData(initialFormData);
    }
  }, [initialFormData]);

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_PRESET_KEY);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await response.json();
      setFormData((prev) => ({ ...prev, image: data.secure_url }));
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files[0];
    const imageUrl = await handleImageUpload(file);
    if (imageUrl) {
      setFormData((prev) => ({ ...prev, image: imageUrl }));
    }
  };

  useEffect(() => {
    console.log("image url", formData);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCancel = () => {
    handleEdit(false);
    setIsEditing(false);
    if (originalFormData) {
      setFormData(originalFormData);
    }
  };

  if (recruiterLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || recruiterError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography color="error">{error || recruiterError}</Typography>
      </Container>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ position: "relative", width: "100%" }}>
        {/* Background Container */}
        <Box
          sx={{
            position: "relative",
            minHeight: "175px",
            overflow: "hidden",
            backgroundImage: `url(${bg_profile})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        ></Box>

        {/* Content Container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            position: "relative",
            marginTop: { xs: "-60px", sm: "-80px", md: "-100px" },
          }}
        >
          {/* Profile Info Container */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "center", md: "center" },
              gap: 2,
              marginTop: "25px",
              width: "100%",
              maxWidth: "1200px",
              px: { xs: "16px", sm: "24px", md: "32px" },
              pb: "2%",
            }}
          >
            <Box position="relative" sx={{ mb: { xs: 2, md: 0 } }}>
              <Box
                sx={{
                  width: { xs: "150px", sm: "180px", md: "200px" },
                  height: { xs: "150px", sm: "180px", md: "200px" },
                  border: "8px solid white",
                  borderRadius: "50%",
                  overflow: "hidden",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor:
                    "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",
                }}
              >
                <img
                  src={formData.image}
                  alt={formData.firstName || t("personalInformation.profile")}
                  referrerPolicy="no-referrer"
                  style={{
                    maxWidth: "140%",
                    maxHeight: "140%",
                    objectFit: "contain",
                    transform: "scale(0.8)",
                  }}
                />
              </Box>

              {isEditing ? (
                <IconButton
                  onClick={handleImageClick}
                  sx={{
                    position: "absolute",
                    bottom: 12,
                    right: 15,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(4px)",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 20 }} />
                </IconButton>
              ) : null}

              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Box>

            {/* Text Content */}
            <ProfileImageContainer>
              <Box
                sx={{
                  pt: { xs: 0, md: 6 },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "#000000",
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: { xs: "24px", sm: "28px", md: "32px" },
                  }}
                >
                  {formData.first_name} {formData.last_name}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    color: "#00000066",
                    fontSize: { xs: "16px", md: "18px" },
                    fontFamily: "Poppins",
                  }}
                >
                  {formData.company}
                </Typography>
              </Box>
            </ProfileImageContainer>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <CompanyInfo>
          <Grid container spacing={2}>
            <Grid item xs={12} md={9}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  fontSize: { xs: "20px", md: "24px" },
                  color: "#000000",
                }}
              >
                {t("navBar.account_settings")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                sx={{
                  width: { xs: "100%", sm: 135 },
                  height: 48,
                  borderRadius: "10px",
                  gap: "10px",
                  paddingTop: "14px",
                  paddingRight: "20px",
                  paddingBottom: "14px",
                  paddingLeft: "20px",
                  backgroundColor: colors.primary,
                  "&:hover": {
                    border: "none",
                    backgroundColor: "rgba(26, 35, 126, 0.04)",
                    "& .MuiTypography-root": {
                      color: colors.primary,
                    },
                    "& img": {
                      filter: `brightness(0) saturate(100%) invert(23%) sepia(74%) saturate(1982%) hue-rotate(220deg) brightness(93%) contrast(98%)`,
                    },
                  },
                }}
                onClick={() => handleEdit(true)}
                disabled={isEditing}
              >
                <img src={edit_icon} alt={t("personalInformation.edit")} />
                <Typography
                  sx={{
                    paddingLeft: 0,
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: "12px",
                    textTransform: "none",
                    color: "white",
                  }}
                >
                  {t("personalInformation.edit")}
                </Typography>
              </Button>
            </Grid>
          </Grid>
        </CompanyInfo>

        <Grid
          container
          spacing={3}
          sx={{
            px: { xs: 0, sm: 2, md: 6 },
            mt: { xs: 1, md: 2 },
          }}
        >
          {filteredFields
            .map(([key, value]) => (
              <Grid item xs={12} key={key}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4} md={3}>
                    <Typography
                      sx={{
                        color: "#00000099",
                        opacity: "80%",
                        fontFamily: "Poppins",
                        fontWeight: 500,
                        fontSize: "14px",
                        mb: { xs: 1, sm: 0 },
                      }}
                    >
                      {t(`userProfile.fields.${key}`)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} md={6} lg={4}>
                    <StyledTextField
                      fullWidth
                      name={key}
                      value={value}
                      onChange={handleChange}
                      variant="outlined"
                      disabled={!isEditing}
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}

          {isEditing && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: { xs: "center", sm: "flex-end" },
                  mt: 4,
                  gap: 2,
                }}
              >
                <UpdateButton
                  variant="contained"
                  disabled={!isEditing || loading}
                  onClick={() => handleSave(userId.userId, formData)}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      <span>{t("personalInformation.updating")}</span>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 400,
                        fontSize: "12px",
                        textTransform: "none",
                      }}
                    >
                      {t("personalInformation.update_profile")}
                    </Typography>
                  )}
                </UpdateButton>

                <CancelButton
                  variant="contained"
                  onClick={handleCancel}
                  disabled={!isEditing || loading}
                  sx={{
                    bgcolor: "#fff",
                    color: "#004F98",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontFamily: "Poppins",
                    px: 2,
                    py: 1,
                    fontWeight: "500",
                    "&:hover": {
                      bgcolor: "#FF0000",
                      "& .MuiTypography-root": {
                        color: "#FFFFFF",
                      },
                      "& img": {
                        filter: "brightness(0) invert(1)",
                      },
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#e0e0e0",
                    },
                    "& .MuiTypography-root": {
                      color: "#FF0000",
                      fontFamily: "Poppins",
                      fontWeight: 400,
                      fontSize: "12px",
                    },
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      <span>{t("personalInformation.updating")}</span>
                    </Box>
                  ) : (
                    <>
                      <img src={cancel_icon} alt={t("general.cancel")} />
                      <Typography>{t("general.cancel")}</Typography>
                    </>
                  )}
                </CancelButton>
              </Box>
            </Grid>
          )}

          <Grid container sx={{ marginTop: { xs: 4, md: 8 } }}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  fontSize: { xs: "20px", md: "24px" },
                  color: "#000000",
                  pl: { xs: 3, sm: 3 },
                  marginBottom: { xs: "24px", md: "44px" },
                  mt: { xs: 6, md: 4 }
                }}
              >
                {t("personalInformation.password_settings")}
              </Typography>
            </Grid>
            <Grid item xs={12}
              sx={{
                ml: 3
              }}>
              <RecruiterPasswordSettings
                formData={formData}
                setFormData={setFormData}
              />
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </PageContainer>
  );
}

export default User;
